import { isNil } from 'lodash-es';
import { openWindow } from '..';
import { dataURLtoBlob, urlToBase64 } from './base64Conver';
import { watermarkConfirm } from '/@/businessComponents/WaterMarkModal';
import { getFileExtension } from '../url';
import { addOssWatermarkUrl } from './_ai-watermark';
import { checkDownloadPermission } from './check-download-permission';

/**
 * Download online pictures
 * @param url
 * @param filename
 * @param mime
 * @param bom
 */
export function downloadByOnlineUrl(url: string, filename: string, mime?: string, bom?: BlobPart) {
  urlToBase64(url).then((base64) => {
    downloadByBase64(base64, filename, mime, bom);
  });
}

/**
 * Download pictures based on base64
 * @param buf
 * @param filename
 * @param mime
 * @param bom
 */
export function downloadByBase64(buf: string, filename: string, mime?: string, bom?: BlobPart) {
  const base64Buf = dataURLtoBlob(buf);
  downloadByData(base64Buf, filename, mime, bom);
}

/**
 * Download according to the background interface file stream
 * @param {*} data
 * @param {*} filename
 * @param {*} mime
 * @param {*} bom
 */
export function downloadByData(data: BlobPart, filename: string, mime?: string, bom?: BlobPart) {
  const blobData = typeof bom !== 'undefined' ? [bom, data] : [data];
  const blob = new Blob(blobData, { type: mime || 'application/octet-stream' });

  const blobURL = window.URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', filename);
  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank');
  }
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
  window.URL.revokeObjectURL(blobURL);
}

/**
 * Download file according to file address
 * @param {*} sUrl
 */
export async function downloadByUrl(options: {
  url: string;
  target?: TargetContext;
  fileName?: string;
  confirmWatermark?: boolean;
  noCheckDownloadPermission?: boolean;
}): Promise<boolean> {
  const {
    url,
    target = '_blank',
    fileName,
    confirmWatermark = true,
    noCheckDownloadPermission = false,
  } = options;
  if (!noCheckDownloadPermission && !(await checkDownloadPermission())) {
    return false;
  }

  const isChrome = window.navigator.userAgent.toLowerCase().indexOf('chrome') > -1;
  const isSafari = window.navigator.userAgent.toLowerCase().indexOf('safari') > -1;

  if (/(iP)/g.test(window.navigator.userAgent)) {
    console.error('Your browser does not support download!');
    return false;
  }

  let downloadUrl = url;
  // 图片类型文件再进行水印问询
  if (confirmWatermark && ['png', 'jpg', 'jpeg', 'webp'].includes(getFileExtension(url))) {
    // 问询用户是否保留水印
    const watermark = await watermarkConfirm();
    if (isNil(watermark)) {
      // 用户取消，退出下载
      return false;
    }
    if (watermark) {
      downloadUrl = await addOssWatermarkUrl({ url });
    }
  }

  if (isChrome || isSafari) {
    downloadByUrlBySetName({
      noCheckDownloadPermission,
      url: downloadUrl,
      name: fileName?.slice(0, 195) || url.substring(url.lastIndexOf('/') + 1, url.length),
    });

    return true;
  }

  openWindow(downloadUrl, { target });
  return true;
}

export async function downloadByUrlBySetName(options: {
  url: string;
  name: string;
  noCheckDownloadPermission?: boolean;
}) {
  const { noCheckDownloadPermission = false } = options;
  if (!noCheckDownloadPermission && !(await checkDownloadPermission())) {
    return false;
  }

  const x = new window.XMLHttpRequest();
  x.open('GET', options.url, true);
  x.responseType = 'blob';
  x.onload = () => {
    const url = window.URL.createObjectURL(x.response);
    const a = document.createElement('a');
    a.href = url;
    a.download = options.name;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  x.send();
}
